/**
 * Screen 4 — Home / Lobby.
 *
 * Mirrors the wireframe: greeting + league row, an eraser-balance chip
 * (tap to buy more), daily-goal progress, quick-match hero, game-mode
 * pills, then the 1-v-1 duels strip, then live + featured contests.
 *
 * Data: a single GET /home aggregate (Phase 7) so a cold open is one
 * round-trip. Sub-sections degrade independently (missing balance, no live,
 * etc.) so we never blank-page on a network blip.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { I, icon } from '../components/icons.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';
import { antiCheatBannerHtml } from '../components/anti_cheat_banner.js';
import { buyEraserBannerHtml } from '../components/buy_eraser_banner.js';
import { buyFiftyfiftyBannerHtml } from '../components/buy_fiftyfifty_banner.js';
import { pollBannerHtml } from '../components/poll_banner.js';
import { referFiftyfiftyBannerHtml } from '../components/refer_fiftyfifty_banner.js';
import { shopBannerHtml } from '../components/shop_banner.js';
import {
  getUnreadCount,
  onNotifications,
} from '../lib/notifications.js';
import {
  ensureChatBadgeStarted,
  getChatUnreadTotal,
  onChatBadge,
} from '../lib/messaging.js';
import { maybePromptForReminders } from '../lib/push.js';
import { maybeStartHomeTour, maybeStartPowerupsTour } from '../lib/tour.js';
import { maybeShowWhatsNew } from '../lib/whats_new.js';
import { duelRowHtml } from './duels_browse.js';

// One-shot guard: when a registered user's pinned trivia transitions to LIVE
// we redirect them into the room. Tracking by id prevents a re-redirect if
// they navigate back to /home after the game starts.
const _autoRedirected = new Set();
let _homePollTimer = null;
let _countdownTicker = null;
let _notifUnsub = null;
let _chatBadgeUnsub = null;

export async function renderHome(root) {
  stopHomeTimers();
  window.addEventListener('popstate', stopHomeTimers, { once: true });

  // Keep the bell badge in lockstep with the singleton notification bus.
  // Unsubscribed by `stopHomeTimers` so navigation away clears the
  // listener (otherwise we'd leak across mounts).
  if (_notifUnsub) _notifUnsub();
  _notifUnsub = onNotifications((event) => {
    if (event.kind === 'unread' || event.kind === 'new') {
      applyNotifBadge();
    }
  });

  // Boot the chat-badge subscription (idempotent) and mirror the
  // notification-badge pattern: subscribe here so any chat.unread
  // event lands on the icon without a re-render.
  ensureChatBadgeStarted();
  if (_chatBadgeUnsub) _chatBadgeUnsub();
  _chatBadgeUnsub = onChatBadge(() => applyChatBadge());

  // First paint from cache (auth.getMe) so users on slow networks see the
  // greeting immediately while /home is in flight.
  const cachedMe = auth.getMe();
  root.innerHTML = shell(cachedMe);
  paintLoading();

  await refreshAndPaint();
  schedulePoll();
}

async function refreshAndPaint() {
  let payload;
  try {
    payload = await api('/home');
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    toast.fromError(err, 'Could not load home.');
    paintError();
    return;
  }
  auth.setMe(payload.me);

  // Auto-redirect to the live trivia room if any pinned trivia just
  // transitioned to LIVE and the caller is registered for it. We pick the
  // first one in the chronologically-sorted list — there's only ever one
  // live-now anyway in practice. Mirrors the one-shot guard /live uses.
  const goingLive = (payload.pinned_trivias || []).find(
    (t) => t.status === 'live' && t.is_registered && !_autoRedirected.has(t.id),
  );
  if (goingLive) {
    _autoRedirected.add(goingLive.id);
    stopHomeTimers();
    toast.success(`${goingLive.title} is starting — let's go!`);
    navigate(`/live/${goingLive.id}/play`);
    return;
  }

  await paintLoaded(payload);
}

function schedulePoll() {
  // Re-fetch /home every 10s so the pinned card's countdown stays fresh
  // and we catch the SCHEDULED → LIVE transition without a manual reload.
  if (_homePollTimer) clearTimeout(_homePollTimer);
  _homePollTimer = setTimeout(async () => {
    if (!document.getElementById('home-body')) return;
    await refreshAndPaint();
    if (document.getElementById('home-body')) schedulePoll();
  }, 10_000);
}

function stopHomeTimers() {
  if (_homePollTimer) { clearTimeout(_homePollTimer); _homePollTimer = null; }
  if (_countdownTicker) { clearInterval(_countdownTicker); _countdownTicker = null; }
  if (_notifUnsub) { _notifUnsub(); _notifUnsub = null; }
  if (_chatBadgeUnsub) { _chatBadgeUnsub(); _chatBadgeUnsub = null; }
}

function startCountdownTicker() {
  if (_countdownTicker) clearInterval(_countdownTicker);
  _countdownTicker = setInterval(() => {
    document.querySelectorAll('[data-countdown-to]').forEach((el) => {
      el.textContent = fmtCountdown(el.dataset.countdownTo);
    });
  }, 1000);
}

function fmtCountdown(targetIso) {
  if (!targetIso) return '';
  const ms = new Date(targetIso).getTime() - Date.now();
  if (ms <= 0) return 'starting…';
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `in ${d}d ${h}h`;
  if (h > 0) return `in ${h}h ${m}m`;
  if (m > 0) return `in ${m}m ${sec}s`;
  return `in ${sec}s`;
}

function shell(me) {
  return `
    <div class="pad" id="home-header">${headerHtml(me, null)}</div>
    <div class="pad" id="home-body" style="padding-top:6px;">
      ${heroSkel()}
      <h3 class="sec">Game modes</h3>
      <div class="row" id="home-modes" data-tour="game-modes">
        <a href="/play/solo" data-link class="btn ghost sm" style="flex:1;width:auto;">Solo</a>
        <a href="/play/duel" data-link class="btn ghost sm" style="flex:1;width:auto;">1-on-1</a>
        <a href="/play" data-link class="btn ghost sm" style="flex:1;width:auto;">Group</a>
      </div>
      <h3 class="sec"><span class="dot"></span>Live &amp; featured</h3>
      <div id="home-live-feed"><div class="skel"></div><div class="skel"></div></div>
    </div>
  `;
}

function paintLoading() { /* nothing extra — shell already shows skeletons. */ }

function paintError() {
  const feed = document.getElementById('home-live-feed');
  // Same defensive pattern: the user may have navigated away by the time the
  // error toast fires; we just no-op.
  if (feed) feed.innerHTML = `<div class="card muted">Could not load — pull to refresh.</div>`;
}

async function paintLoaded(payload) {
  const { me, balance_kobo, bonus_balance_kobo, featured_contests, pinned_trivias, live_now, upcoming, previous_live, home_banner, home_hero, active_poll, restriction_notice } = payload;
  // The /home fetch can resolve AFTER the user has already navigated away,
  // at which point our shell is gone. Guard against it.
  const header = document.getElementById('home-header');
  const body = document.getElementById('home-body');
  if (!header || !body) return;
  header.innerHTML = headerHtml(me, balance_kobo, bonus_balance_kobo);
  // Admin-toggled banner mode — six mutually exclusive options.
  // Server sends `home_banner: 'eraser' | 'fiftyfifty' |
  // 'buy_fiftyfifty' | 'shop' | 'poll' | 'anti_cheat'`. Only one
  // renders at a time. Fall back to the eraser banner if the field is
  // missing or unknown so an older client / cold cache still renders
  // something sensible.
  let bannerHtml;
  if (home_banner === 'poll' && active_poll) {
    bannerHtml = pollBannerHtml(active_poll);
  } else if (home_banner === 'anti_cheat') {
    bannerHtml = antiCheatBannerHtml();
  } else if (home_banner === 'shop') {
    bannerHtml = shopBannerHtml();
  } else if (home_banner === 'buy_fiftyfifty') {
    bannerHtml = buyFiftyfiftyBannerHtml();
  } else if (home_banner === 'fiftyfifty') {
    bannerHtml = referFiftyfiftyBannerHtml();
  } else {
    bannerHtml = buyEraserBannerHtml();
  }
  // Admin-toggled jumbotron. Server sends `home_hero: 'duel' | 'practice'`
  // based on the `home_hero_show_practice` feature flag. Fall back to the
  // duel hero on an unknown / missing value so an older client / cold
  // cache still renders something sensible.
  const heroHtml = home_hero === 'practice'
    ? practiceHero()
    : quickMatchHero(balance_kobo);
  body.innerHTML = `
    ${restrictionBannerHtml(restriction_notice)}
    ${bannerHtml}
    ${eraserAwardNoticeHtml(previous_live)}
    ${pinnedTriviasStrip(pinned_trivias)}
    ${heroHtml}
    <h3 class="sec">Game modes</h3>
    <div class="row" id="home-modes" data-tour="game-modes">
      <a href="/play/solo" data-link class="btn ghost sm" style="flex:1;width:auto;">Solo</a>
      <a href="/play/duel" data-link class="btn ghost sm" style="flex:1;width:auto;">1-on-1</a>
      <a href="/play" data-link class="btn ghost sm" style="flex:1;width:auto;">Group</a>
    </div>
    ${duelsStripPlaceholder()}
    ${liveAndFeaturedHtml(live_now, upcoming, featured_contests, me)}
    ${previousLiveSection(previous_live)}
    ${communityCard()}
  `;
  wirePinnedTrivias(pinned_trivias);
  wireEraserAwardDismiss(previous_live);
  startCountdownTicker();
  // Await the duels strip so its `data-tour="duels-strip"` anchor exists
  // when the tour engine pre-filters steps. Without this the tour would
  // silently drop the duels step on cold starts where the /duels/home
  // fetch hasn't resolved by tour init. Failures inside
  // loadAndPaintDuelsStrip are swallowed (discovery surface, not
  // critical), so awaiting can't stall the rest of the page.
  await loadAndPaintDuelsStrip();
  // After a successful paint, offer the first-run tour. The helper bails
  // instantly if the local-done flag or `me.tour_completed` is set, so it's
  // safe to call on every refreshAndPaint.
  maybeStartHomeTour({ me });
  // Phase E — the power-ups + notifications tour. Its internal gate
  // requires `me.tour_completed` to be true, so this is a no-op until
  // the user has finished the first tour (new users: second /home
  // load; existing users: next /home load).
  maybeStartPowerupsTour({ me });
}

// ---- Phase 8.12 async duels strip -----------------------------------------

function duelsStripPlaceholder() {
  return `<div id="duels-strip"></div>`;
}

async function loadAndPaintDuelsStrip() {
  const host = document.getElementById('duels-strip');
  if (!host) return;
  let payload;
  try {
    payload = await api('/duels/home');
  } catch {
    // Auth blip / offline / server error — strip just doesn't
    // render. Discovery surface, not critical to Home, so we
    // degrade silently.
    return;
  }
  // Once we have the /duels/home payload we know whether the async
  // duels feature is live for this user — pass it into the "what's
  // new" engine so a feature-gated announcement only nudges users
  // when the feature actually exists for them. Deferred to here (not
  // fired alongside the tours in paintLoaded) so the flag check is
  // authoritative.
  try {
    maybeShowWhatsNew({ me: auth.getMe(), duelsHomePayload: payload });
  } catch { /* announcement engine failures must not break Home */ }
  // When the async-duels feature flag is off, the backend returns
  // `feature_enabled: false, items: []` — the whole section stays
  // hidden. When the flag is on but no duels exist yet, we render
  // the header + a "Create the first duel" CTA so discovery isn't
  // gated behind seeing someone else's duel first.
  if (payload?.feature_enabled === false) return;
  const items = payload?.items || [];

  const headerHtml = `
    <div class="row" data-tour="duels-strip" style="align-items:center;margin-top:14px">
      <h3 class="sec" style="margin:0;flex:1">1-v-1 Duels</h3>
      <a href="/duels" data-link style="font-size:12px;color:var(--teal-t);font-weight:600">View all →</a>
    </div>
  `;

  const stripBody = items.length
    ? items
        .map((d) => {
          // Pick the row variant that matches the viewer's relationship
          // to this duel: opponent whose turn it is → 'your-turn', creator
          // whose duel is in flight → 'waiting', anything else on the
          // strip is a public duel open to join.
          if (d.viewer_role === 'opponent') return duelRowHtml(d, 'your-turn');
          if (d.viewer_role === 'creator') return duelRowHtml(d, 'waiting');
          return duelRowHtml(d, 'open');
        })
        .join('')
    : `
      <div class="card" style="padding:14px;text-align:center;border-style:dashed">
        <div style="font-size:13px;color:#fff;font-weight:600;margin-bottom:4px">No duels yet</div>
        <div style="font-size:11.5px;color:var(--mute);font-weight:500;margin-bottom:11px">
          Be the first — publish a duel and win real money.
        </div>
        <a href="/duels/new" data-link class="btn teal sm"
          style="margin:0;width:auto;padding:8px 20px;display:inline-block">Create a duel</a>
      </div>
    `;

  // Previous-duel card renders whenever the backend surfaces one.
  // We PREFER `me.id` to identify the viewer's row, but fall back to
  // `viewer_role` from the payload — the backend already tells us
  // which side the caller was on, and we can derive `myId` from
  // duel.creator / duel.opponent using that role. Without the
  // fallback, an /me cache miss on cold-start would silently hide
  // the card for existing users.
  const me = auth.getMe();
  const previousDuelHtml = payload?.previous_duel
    ? previousDuelSection(payload.previous_duel, me?.id)
    : '';
  // Render strip AND previous card — the two are independent surfaces.
  // Previously we returned early on empty items and never got here,
  // so users with finished duels but no in-flight ones saw nothing.
  host.innerHTML = `${headerHtml}${stripBody}${previousDuelHtml}`;
  // The row buttons carry `data-play-id` / `data-join-id` /
  // `data-view-id` — wire handlers so a tap on Play jumps into the
  // run, a tap on Join escrows + goes into the joiner's turn, and a
  // tap on View (the creator-is-waiting row) opens the status page.
  host.querySelectorAll('[data-play-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      navigate(`/duel/${btn.dataset.playId}/play`);
    });
  });
  host.querySelectorAll('[data-view-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      navigate(`/duel/${btn.dataset.viewId}`);
    });
  });
  host.querySelectorAll('[data-join-id]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      btn.textContent = 'Joining…';
      try {
        await api(`/duels/${btn.dataset.joinId}/join`, { method: 'POST' });
        navigate(`/duel/${btn.dataset.joinId}/play`);
      } catch (err) {
        if (err instanceof ApiError && err.code === 'already_taken') {
          btn.textContent = 'Taken';
        } else {
          btn.disabled = false;
          btn.textContent = 'Join';
          toast.error(err.message || 'Could not join.');
        }
      }
    });
  });
}


// ---- previous 1-v-1 duel (most recent finished participation) -------------
//
// Mirrors `previousLiveSection` for async duels. Shows the most recent
// completed / forfeited duel with topic + opponent + score + payout
// tag if won. Whole card is a link into the duel status page; the
// "View all" header link opens the profile duel-history page.

function previousDuelSection(duel, myId) {
  if (!duel) return '';
  // Determine which side "I" was on. Preferred path: match by
  // explicit user id. Fallback path: use `viewer_role` from the
  // payload — the backend attaches it and it's authoritative on
  // who the viewer was.
  const roleMe = duel.viewer_role === 'opponent' ? duel.opponent?.id : duel.creator?.id;
  const effectiveMyId = myId || roleMe || null;
  const runsById = new Map((duel.runs || []).map((r) => [r.user_id, r]));
  const myRun = effectiveMyId ? (runsById.get(effectiveMyId) || null) : null;
  const otherId = duel.creator?.id === effectiveMyId ? duel.opponent?.id : duel.creator?.id;
  const otherRun = otherId ? runsById.get(otherId) : null;
  const otherPlayer = duel.creator?.id === effectiveMyId ? duel.opponent : duel.creator;
  const myScore = myRun?.score ?? 0;
  const otherScore = otherRun?.score ?? 0;
  const iWon = duel.winner_id === effectiveMyId;
  const isTie = duel.status === 'completed' && !duel.winner_id;
  const payoutKobo = iWon ? (duel.money?.payout_kobo || 0) : 0;
  const payoutTag = payoutKobo > 0
    ? `<span style="display:inline-flex;align-items:center;padding:3px 8px;background:#3a2a14;border:1px solid rgba(229,193,0,0.45);border-radius:6px;font-family:var(--font-display);font-size:11px;color:#E5C100;font-weight:700;white-space:nowrap;">
         +${escape(formatKobo(payoutKobo))}
       </span>`
    : '';
  const outcomeLabel = isTie ? 'Draw' : (iWon ? 'You won' : 'You lost');
  const outcomeColor = isTie ? 'var(--mute)' : (iWon ? '#5FE0BE' : '#FF8FB6');
  const topic = escape(duel.category_name || 'Trivia');
  const opp = escape(otherPlayer?.username || 'Player');
  const finishedAgo = duel.settled_at ? escape(fmtPastWhen(duel.settled_at)) : '';
  return `
    <div class="row" style="align-items:center;margin-top:14px">
      <h3 class="sec" style="margin:0;flex:1">Previous 1-v-1</h3>
      <a href="/u/${escape(effectiveMyId || '')}?tab=duels" data-link
         style="font-size:12px;color:var(--teal-t);font-weight:600">View all →</a>
    </div>
    <a href="/duel/${escape(duel.id)}" data-link class="card"
       style="padding:14px;text-decoration:none;display:block;background:var(--surf-2);">
      <div class="row" style="align-items:center;gap:12px;">
        <div class="grow" style="min-width:0;">
          <div style="font-size:14px;color:#fff;font-weight:700;font-family:var(--font-display);">
            vs ${opp}
          </div>
          <div class="muted" style="font-size:11px;margin-top:3px;">
            ${topic} · ${myScore} – ${otherScore}${finishedAgo ? ` · ${finishedAgo}` : ''}
          </div>
        </div>
        <div style="text-align:right;display:flex;flex-direction:column;gap:4px;align-items:flex-end;">
          <div style="font-family:var(--font-display);font-size:13px;color:${outcomeColor};font-weight:700;line-height:1;">
            ${outcomeLabel}
          </div>
          ${payoutTag}
        </div>
        <div style="color:var(--mute);font-size:18px;line-height:1;">›</div>
      </div>
    </a>
  `;
}


// ---- previous live trivia (most recent finished participation) -------------

function previousLiveSection(pl) {
  if (!pl) return '';
  const payoutTag = (pl.your_payout_kobo || 0) > 0
    ? `<span style="display:inline-flex;align-items:center;padding:3px 8px;background:#3a2a14;border:1px solid rgba(229,193,0,0.45);border-radius:6px;font-family:var(--font-display);font-size:11px;color:#E5C100;font-weight:700;white-space:nowrap;">
         +${escape(formatKobo(pl.your_payout_kobo))}
       </span>`
    : '';
  const rankLabel = pl.your_rank ? `#${pl.your_rank}` : '—';
  return `
    <h3 class="sec">Previous live trivia</h3>
    <a href="/live/${escape(pl.id)}/results" data-link class="card"
       data-tour="previous-live"
       style="padding:14px;text-decoration:none;display:block;background:var(--surf-2);">
      <div class="row" style="align-items:center;gap:12px;">
        <div class="grow" style="min-width:0;">
          <div style="font-size:14px;color:#fff;font-weight:700;font-family:var(--font-display);">
            ${escape(pl.title || 'Live trivia')}
          </div>
          <div class="muted" style="font-size:11px;margin-top:3px;">
            ${pl.participants_count} ${pl.participants_count === 1 ? 'player' : 'players'} ·
            ${pl.question_count} questions${pl.finished_at ? ` · ${escape(fmtPastWhen(pl.finished_at))}` : ''}
          </div>
        </div>
        <div style="text-align:right;display:flex;flex-direction:column;gap:4px;align-items:flex-end;">
          <div style="font-family:var(--font-display);font-size:16px;color:#fff;font-weight:700;line-height:1;">
            ${escape(rankLabel)}
          </div>
          ${payoutTag}
        </div>
        <div style="color:var(--mute);font-size:18px;line-height:1;">›</div>
      </div>
    </a>
  `;
}

function fmtPastWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Math.max(0, Date.now() - d.getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' });
}

// ---- account-state restriction banner ---------------------------------------
//
// Rendered above every other home banner when /home returns a
// restriction_notice (currently only fires for under_investigation —
// banned users never reach /home because /auth/login blocks them).
// Carries the admin-authored message inline so the user sees the
// specific reason, not just the canned 24-72h line.

function restrictionBannerHtml(notice) {
  if (!notice) return '';
  const headline = escape(notice.headline || 'Your account is under review');
  const body = escape(notice.body || '');
  const adminMessage = (notice.admin_message || '').trim();
  const adminHtml = adminMessage
    ? `<div style="margin-top:10px;padding:10px 12px;background:rgba(0,0,0,.24);border-left:3px solid #FBBF24;border-radius:8px;font-size:13px;line-height:1.55;color:#FEF3C7;white-space:pre-wrap;">${escape(adminMessage)}</div>`
    : '';

  // "Defend your score" state — an active re-test exists, so we swap the
  // pure text banner for one carrying a countdown to `deadline_at` and a
  // deep-link to `/retest`. The countdown reuses the existing home-page
  // `data-countdown-to` ticker so we don't stand up a second interval.
  // For the submitted state we drop the countdown + link (the user
  // has already done their part) and just render the reassurance copy.
  // Falls through to the plain admin-message body when `retest` is null.
  const retest = notice.retest;
  let retestHtml = '';
  if (retest && (retest.status === 'sent' || retest.status === 'in_progress')) {
    const deadlineIso = retest.deadline_at || '';
    retestHtml = `
      <div style="margin-top:12px;display:flex;flex-wrap:wrap;align-items:center;gap:10px;">
        <a href="/retest" data-link
           style="display:inline-flex;align-items:center;gap:6px;padding:9px 14px;background:#FBBF24;color:#7C2D12;font-weight:700;font-size:13px;border-radius:8px;text-decoration:none;">
          ${retest.status === 'in_progress' ? 'Resume re-test' : 'Take the re-test'} →
        </a>
        <span style="font-size:12px;color:#FEF3C7;">
          Time left:
          <b data-countdown-to="${escape(deadlineIso)}"
             style="font-family:var(--font-display);color:#FFF;">…</b>
        </span>
      </div>
    `;
  } else if (retest && retest.status === 'submitted') {
    retestHtml = `
      <div style="margin-top:12px;display:inline-flex;align-items:center;gap:8px;padding:8px 12px;background:rgba(0,0,0,.24);border:1px solid rgba(251,191,36,.35);border-radius:8px;font-size:12px;color:#FEF3C7;">
        <span style="font-size:14px;">📬</span>
        <span>Submission received. Admin will confirm shortly.</span>
      </div>
    `;
  }

  return `
    <div class="card" style="background:linear-gradient(135deg,#7C2D12,#B45309);border:1px solid rgba(251,191,36,.5);color:#FEF3C7;padding:14px 16px;margin-bottom:12px;">
      <div style="display:flex;align-items:center;gap:10px;">
        <div style="font-size:22px;line-height:1;">${retest && retest.status !== 'submitted' ? '🛡️' : retest ? '📬' : '⚠️'}</div>
        <div style="font-weight:700;font-size:15px;color:#FFF;">${headline}</div>
      </div>
      <div style="margin-top:6px;font-size:13px;line-height:1.5;color:#FEF3C7;">${body}</div>
      ${adminHtml}
      ${retestHtml}
    </div>
  `;
}

// ---- dismissable eraser-award notice -----------------------------------------

const ERASER_NOTICE_DISMISSED_KEY = 'triviana.eraser_award_dismissed_v1';

function eraserAwardDismissedIds() {
  try {
    return new Set(JSON.parse(localStorage.getItem(ERASER_NOTICE_DISMISSED_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function rememberDismissedEraserAward(id) {
  try {
    const set = eraserAwardDismissedIds();
    set.add(id);
    localStorage.setItem(
      ERASER_NOTICE_DISMISSED_KEY,
      JSON.stringify(Array.from(set).slice(-50)), // bound the list
    );
  } catch { /* localStorage unavailable — silently fail */ }
}

function eraserAwardNoticeHtml(pl) {
  if (!pl || (pl.your_erasers_awarded || 0) <= 0) return '';
  if (eraserAwardDismissedIds().has(pl.id)) return '';
  const count = pl.your_erasers_awarded;
  return `
    <div id="eraser-award-notice" class="card"
         style="padding:12px 14px;margin-bottom:10px;background:rgba(229,193,0,0.10);border:1px solid rgba(229,193,0,0.45);">
      <div class="row" style="align-items:center;gap:12px;">
        <div style="font-size:22px;line-height:1;">🎉</div>
        <div class="grow" style="min-width:0;">
          <div style="font-size:13px;color:#fff;font-weight:700;">
            You won ${count} eraser${count === 1 ? '' : 's'}!
          </div>
          <div class="muted" style="font-size:11px;line-height:1.4;margin-top:1px;">
            From <b style="color:#fff;">${escape(pl.title || 'Live trivia')}</b>. Credited to your inventory.
          </div>
        </div>
        <a href="/shop" data-link
           style="font-family:var(--font-display);font-size:11px;text-decoration:none;color:#E5C100;font-weight:700;background:rgba(229,193,0,0.15);padding:5px 10px;border-radius:6px;white-space:nowrap;">
          View
        </a>
        <button type="button" id="eraser-award-dismiss" data-live-id="${escape(pl.id)}"
                aria-label="Dismiss"
                style="background:transparent;border:none;color:var(--mute);font-size:18px;line-height:1;cursor:pointer;padding:4px 6px;">
          ×
        </button>
      </div>
    </div>
  `;
}

function wireEraserAwardDismiss(pl) {
  if (!pl) return;
  const btn = document.getElementById('eraser-award-dismiss');
  if (!btn) return;
  btn.addEventListener('click', () => {
    const id = btn.dataset.liveId;
    if (id) rememberDismissedEraserAward(id);
    document.getElementById('eraser-award-notice')?.remove();
  });
}

/**
 * Render the stacked pinned-trivia hero strip. Trivias are already
 * server-sorted earliest-first; each one re-uses the same magenta
 * `.hero pink` treatment used elsewhere for LIVE content.
 */
function pinnedTriviasStrip(pinnedList) {
  if (!Array.isArray(pinnedList) || pinnedList.length === 0) return '';
  return pinnedList.map(pinnedTriviaHero).join('');
}

/**
 * Telegram community invite. Renders at the bottom of the home screen so
 * the live + featured content still owns the top of the fold. Telegram
 * invite URLs are intentionally shareable — the token in the path is the
 * join mechanism, not a secret — so it's fine to ship it in the bundle.
 */
function communityCard() {
  const url = 'https://t.me/+TiSMznf_EJg4OWI0';
  return `
    <h3 class="sec">Community</h3>
    <a href="${url}" target="_blank" rel="noopener noreferrer" class="card" style="text-decoration:none;display:block;padding:14px;background:linear-gradient(135deg,#229ED9 0%,#1B7CB0 100%);border-color:rgba(255,255,255,0.15);">
      <div class="row">
        <div style="font-size:28px;margin-right:6px;">✈️</div>
        <div class="grow">
          <b style="font-family:var(--font-display);font-size:14px;color:#fff;font-weight:600;">
            Join the Triviana community
          </b>
          <div style="font-size:11.5px;color:rgba(255,255,255,0.85);font-weight:500;margin-top:2px;">
            Live trivia alerts, banter, and a heads-up on new prizes — on Telegram.
          </div>
        </div>
        <span class="tag" style="background:rgba(0,0,0,0.25);color:#fff;font-family:var(--font-display);">
          Join →
        </span>
      </div>
    </a>
  `;
}

/**
 * Renders the featured / pinned live trivia at the very top of the home
 * screen. We use the same magenta `.hero pink` styling as the LIVE-now
 * card on /live so the visual language stays consistent. The card
 * disappears automatically once the trivia finishes (the server stops
 * returning `pinned_trivia` for finished trivias).
 */
function pinnedTriviaHero(t) {
  if (!t) return '';
  const isLive = t.status === 'live';
  // Use scheduled_at while waiting, switch to started_at once we're live.
  const countTo = isLive ? t.started_at : t.scheduled_at;
  const tag = isLive
    ? `<span class="tag" style="background:rgba(0,0,0,0.25);color:#fff;">🔴 LIVE NOW</span>`
    : `<span class="tag" style="background:rgba(255,255,255,0.2);color:#fff;">📌 NEXT TRIVIA</span>`;
  const countdownHtml = countTo
    ? `<span class="tag" style="background:rgba(0,0,0,0.25);color:#fff;font-family:var(--font-display);" data-countdown-to="${escape(countTo)}">${escape(fmtCountdown(countTo))}</span>`
    : '';
  const entryLabel = t.entry_fee_kobo
    ? `Entry ${escape(formatKobo(t.entry_fee_kobo))}`
    : 'FREE';
  // CTA copy + behaviour depends on (status, is_registered):
  //   live + registered     → Enter
  //   live + not registered → late-join lock (registration was the gate)
  //   scheduled + registered → Joined ✓ (links to room to view countdown)
  //   scheduled + not registered → Join (POST /live/{id}/join, then live)
  let ctaHtml;
  if (isLive && t.is_registered) {
    ctaHtml = `<a href="/live/${escape(t.id)}/play" data-link class="btn gold" style="margin-bottom:2px;">Enter</a>`;
  } else if (isLive && !t.is_registered) {
    ctaHtml = `<button type="button" class="btn gold" disabled style="margin-bottom:2px;opacity:0.55;cursor:not-allowed;">Started — joining closed</button>`;
  } else if (t.is_registered) {
    // Detail page handles the "waiting on countdown" view + lets the user
    // re-pick erasers right up to pregame.
    ctaHtml = `<a href="/live/${escape(t.id)}" data-link class="btn gold" style="margin-bottom:2px;">Joined ✓ · View</a>`;
  } else {
    // Send to the detail page where they pick their erasers + tap Join.
    ctaHtml = `<a href="/live/${escape(t.id)}" data-link class="btn gold" style="margin-bottom:2px;">Join</a>`;
  }
  return `
    <div class="hero pink" data-tour="live-hero">
      <div class="row">
        ${tag}
        <div class="sp"></div>
        ${countdownHtml}
      </div>
      <div style="font-family:var(--font-display);font-size:18px;font-weight:600;margin:9px 0 2px;">
        ${escape(t.title)}
      </div>
      <div style="font-size:12px;font-weight:500;opacity:0.93;">
        ${t.question_count} Qs · ${escape(entryLabel)} ·
        ${t.participants_count} ${t.participants_count === 1 ? 'player' : 'players'}
      </div>
      <div style="font-family:var(--font-display);color:#FFE9A6;font-size:21px;font-weight:700;margin-top:8px;">
        ${escape(formatKobo(t.prize_kobo))} prize pool
      </div>
      ${ctaHtml}
    </div>
  `;
}

function wirePinnedTrivias(pinnedList) {
  if (!Array.isArray(pinnedList) || pinnedList.length === 0) return;
  document.querySelectorAll('.pinned-join').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.id;
      btn.disabled = true;
      const original = btn.textContent;
      btn.textContent = 'Joining…';
      try {
        await api(`/live/${id}/join`, { method: 'POST' });
        toast.success('Joined — see you at start time.');
        // High-intent moment for push permission — they explicitly
        // signed up for a future event, so a reminder is on-brand.
        void maybePromptForReminders().then((r) => {
          if (r.ok && r.reason !== 'already_granted') {
            toast.success("We'll remind you 30 & 5 min before it starts.");
          }
        });
        // Re-render so the card flips to "Joined ✓".
        await refreshAndPaint();
      } catch (err) {
        btn.disabled = false;
        btn.textContent = original;
        toast.error(err.message || 'Could not join.');
      }
    });
  });
}

// ---- header ----------------------------------------------------------------

function greeting(me) {
  if (!me) return 'Welcome to Triviana';
  const handle = me.username || (me.email ? me.email.split('@')[0] : null);
  return handle ? `Hi, ${handle}` : 'Hi 👋';
}

function leagueLine(me) {
  // Ranks/leagues land in a later phase. Until then, derive a placeholder so
  // the layout matches the wireframe; once `/ranks` returns real data we
  // swap this for the real string.
  if (!me) return '—';
  return 'Newcomer League · Welcome 🎉';
}

function headerHtml(me, balance_kobo, bonus_balance_kobo) {
  const adminBadge = me?.is_admin
    ? '<a href="/admin/questions" data-link class="tag vio" style="margin-left:6px;text-decoration:none;">ADMIN</a>'
    : '';
  // Small "BETA" pill sits inline with the greeting so users always know
  // they're on a pre-1.0 build. Gold-on-violet matches the brand palette.
  const betaBadge =
    '<span class="app-header-beta" title="Triviana is in public beta">BETA</span>';
  // Bonus balance always rendered (₦0.00 in the empty state). Mirrors the
  // "always-visible" treatment on /wallet so users discover the second
  // balance from the home screen, before they even tap into the wallet.
  // Linked to /wallet so a tap surfaces the full explanatory copy.
  const bonusKobo = Number(bonus_balance_kobo || 0);
  return `
    <div class="row">
      <div>
        <div style="font-family:var(--font-display);font-size:18px;color:#fff;font-weight:600;">
          ${escape(greeting(me))}${betaBadge}${adminBadge}
        </div>
        <div style="font-size:11px;color:var(--mute);font-weight:500;margin-top:1px;">
          ${escape(leagueLine(me))}
        </div>
      </div>
      <div class="sp"></div>
      ${chatIconHtml()}
      ${notifBellHtml()}
      ${hudHtml(me)}
    </div>
    <div class="card" style="margin:13px 0 0;">
      <div class="row" style="margin-bottom:8px;align-items:flex-start;">
        <b style="font-family:var(--font-display);font-size:13px;font-weight:600;">Daily goal</b>
        <div class="sp"></div>
        <a href="/wallet" data-link style="text-align:right;text-decoration:none;line-height:1.2;">
          <div style="font-size:12px;font-weight:600;color:var(--gold-t);">
            ${balance_kobo == null ? '—' : escape(formatKobo(balance_kobo))} balance
          </div>
          <div title="Promo credit. Spends first on duels & power-ups. Not withdrawable." style="font-size:11px;font-weight:600;color:#E94BA2;margin-top:2px;">
            🎁 ${escape(formatKobo(bonusKobo))} bonus
          </div>
        </a>
      </div>
      <div class="xpbar"><i style="width:${balance_kobo > 0 ? 70 : 12}%;"></i></div>
    </div>
  `;
}

// ----- notification bell --------------------------------------------------
//
// Bell sits inline with the HUD; tap opens the centre. The red dot reflects
// the global unread count (updated in real time via /ws/notifications). We
// render the badge unconditionally with display:none so the WS update can
// flip it on without a re-render of the whole header.

function notifBellHtml() {
  const count = getUnreadCount();
  const visible = count > 0;
  const text = count > 99 ? '99+' : String(count);
  return `
    <a href="/notifications" data-link
       class="ic"
       id="home-notif-bell"
       data-tour="notif-bell"
       aria-label="Notifications"
       style="position:relative;margin-right:9px;text-decoration:none;color:inherit;cursor:pointer;">
      ${icon(I.bell, { size: 18 })}
      <span id="home-notif-badge"
            style="position:absolute;top:-4px;right:-4px;min-width:15px;height:15px;
                   border-radius:8px;background:var(--pink);border:2px solid #14101F;
                   font-size:8px;line-height:11px;color:#fff;text-align:center;
                   font-weight:700;font-family:var(--font-display);padding:0 3px;
                   display:${visible ? 'inline-block' : 'none'};">${escape(text)}</span>
    </a>
  `;
}

// Phase 8.12 — chat icon + badge. Mirrors notifBellHtml + applyNotifBadge
// but subscribes to the messaging library's per-conversation unread
// accumulator (routed from /ws/notifications' chat.unread events).
function chatIconHtml() {
  const count = getChatUnreadTotal();
  const visible = count > 0;
  const text = count > 99 ? '99+' : String(count);
  return `
    <a href="/messages" data-link
       class="ic"
       id="home-chat-icon"
       aria-label="Messages"
       style="position:relative;margin-right:9px;text-decoration:none;color:inherit;cursor:pointer;">
      ${icon(I.chat, { size: 18 })}
      <span id="home-chat-badge"
            style="position:absolute;top:-4px;right:-4px;min-width:15px;height:15px;
                   border-radius:8px;background:var(--pink);border:2px solid #14101F;
                   font-size:8px;line-height:11px;color:#fff;text-align:center;
                   font-weight:700;font-family:var(--font-display);padding:0 3px;
                   display:${visible ? 'inline-block' : 'none'};">${escape(text)}</span>
    </a>
  `;
}

function applyChatBadge() {
  const el = document.getElementById('home-chat-badge');
  if (!el) return;
  const count = getChatUnreadTotal();
  if (count <= 0) {
    el.style.display = 'none';
    el.textContent = '';
    return;
  }
  el.style.display = 'inline-block';
  el.textContent = count > 99 ? '99+' : String(count);
}

function applyNotifBadge() {
  const el = document.getElementById('home-notif-badge');
  if (!el) return;
  const count = getUnreadCount();
  if (count <= 0) {
    el.style.display = 'none';
    el.textContent = '';
    return;
  }
  el.style.display = 'inline-block';
  el.textContent = count > 99 ? '99+' : String(count);
}

// Stylized eraser glyph — tilted block + base line, currentColor so it
// inherits the chip's gold text colour. Read at 14px in the HUD chip,
// reads at 18-22px elsewhere (the same SVG scales cleanly either way).
const ERASER_GLYPH = `
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
       stroke="currentColor" stroke-width="2.2"
       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"
       style="display:inline-block;vertical-align:middle;margin-top:-2px;">
    <path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21"/>
    <path d="M22 21H7"/>
    <path d="m5 11 9 9"/>
  </svg>
`;

// 50/50 power-up glyph — half-filled ring. Matches the icon registered
// in components/icons.js so the notification centre + home HUD look
// the same. Currentcolor inherits the chip's pink-tinted text colour.
const FIFTYFIFTY_GLYPH = `
  <svg viewBox="0 0 24 24" width="14" height="14"
       aria-hidden="true"
       style="display:inline-block;vertical-align:middle;margin-top:-2px;">
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2.2"/>
    <path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor" stroke="none"/>
    <line x1="12" y1="3" x2="12" y2="21" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
  </svg>
`;

function hudHtml(me) {
  // Two live chips — eraser balance (gold) and 50/50 balance (pink).
  // Tapping eraser opens the eraser shop; tapping 50/50 opens the Invite
  // & Earn page (the only way to mint 50/50s in this phase). Both share
  // the same single-glyph + count visual rhythm.
  const eraserBalance = Number(me?.eraser_balance || 0);
  const fiftyfiftyBalance = Number(me?.fiftyfifty_balance || 0);
  // `data-tour="eraser-hud"` is the existing first-tour anchor (covers
  // the whole HUD pair). The two child anchors `powerups-eraser` and
  // `powerups-fiftyfifty` are Phase E targets — the second tour
  // spotlights each chip individually so the copy can be specific.
  // Both chips deep-link to /shop — the unified power-up storefront —
  // instead of /erasers + /invite so the user has one destination for
  // any power-up-related tap.
  return `<div class="hud" data-tour="eraser-hud" style="display:inline-flex;gap:8px;">
    <a href="/shop" data-link data-tour="powerups-eraser" style="text-decoration:none;">
      <span class="chip gold" title="Erasers — tap to shop"
            style="cursor:pointer;font-family:var(--font-display);font-weight:700;display:inline-flex;align-items:center;gap:4px;">
        ${ERASER_GLYPH}
        <span>${eraserBalance}</span>
      </span>
    </a>
    <a href="/shop" data-link data-tour="powerups-fiftyfifty" style="text-decoration:none;">
      <span class="chip"
            title="50/50 power-ups — tap to shop"
            style="cursor:pointer;font-family:var(--font-display);font-weight:700;display:inline-flex;align-items:center;gap:4px;background:rgba(233,75,162,0.14);border:1px solid rgba(233,75,162,0.45);color:#FF8FB6;">
        ${FIFTYFIFTY_GLYPH}
        <span>${fiftyfiftyBalance}</span>
      </span>
    </a>
  </div>`;
}

// ---- quick match hero ------------------------------------------------------

function quickMatchHero(balance) {
  const subtitle = balance > 0
    ? `Wallet: ${escape(formatKobo(balance))} · stake or play free`
    : 'Play free or top up your wallet to stake real money';
  return `
    <div class="hero violet">
      <span class="tag" style="background:rgba(255,255,255,0.18);color:#fff;">Quick match</span>
      <div style="font-family:var(--font-display);font-size:18px;font-weight:600;margin:9px 0 2px;">
        Play a 1-on-1 now
      </div>
      <div style="font-size:12px;font-weight:500;opacity:0.92;">${subtitle}</div>
      <a href="/play/duel" data-link class="btn gold" style="margin-bottom:2px;">Find opponent</a>
    </div>
  `;
}

function practiceHero() {
  return `
    <div class="hero violet">
      <span class="tag" style="background:rgba(255,255,255,0.18);color:#fff;">🧠 Practice</span>
      <div style="font-family:var(--font-display);font-size:18px;font-weight:600;margin:9px 0 2px;">
        Solo practice
      </div>
      <div style="font-size:12px;font-weight:500;opacity:0.92;">
        Sharpen up for live trivias — free reps, no stakes.
      </div>
      <a href="/play/solo" data-link class="btn gold" style="margin-bottom:2px;">Start practising</a>
    </div>
  `;
}

// ---- live + featured -------------------------------------------------------

function heroSkel() {
  return `
    <div class="hero violet" aria-hidden="true">
      <span class="tag" style="background:rgba(255,255,255,0.18);color:#fff;">Quick match</span>
      <div style="font-family:var(--font-display);font-size:18px;font-weight:600;margin:9px 0 2px;">Play a 1-on-1 now</div>
      <div style="font-size:12px;font-weight:500;opacity:0.92;">Loading your wallet…</div>
      <a href="/play/duel" data-link class="btn gold" style="margin-bottom:2px;">Find opponent</a>
    </div>
  `;
}

function liveAndFeaturedHtml(liveNow, upcoming, contests, me) {
  const liveCards = (liveNow || []).map(liveCard).join('');
  const contestCards = (contests || []).map(contestCard).join('');
  const upcomingCards = (upcoming || []).slice(0, 2).map(upcomingCard).join('');

  // Composite empty state. We always offer at least the "challenge a friend"
  // CTA when there's no other content so the home screen never feels dead.
  if (!liveCards && !contestCards && !upcomingCards) {
    return `
      <div data-tour="live-list">
        <h3 class="sec"><span class="dot"></span>Live &amp; featured</h3>
        <div class="card center muted">
          No live trivias or open paid contests right now.<br>
          <a href="/play/duel" data-link style="margin-top:8px;display:inline-block;">Start a private challenge →</a>
        </div>
      </div>
      ${me?.is_admin ? adminQuickLinks() : ''}
    `;
  }

  return `
    <div data-tour="live-list">
      <h3 class="sec"><span class="dot"></span>Live &amp; featured</h3>
      ${liveCards}
      ${contestCards}
      ${upcomingCards}
      <div style="text-align:center;margin:14px 0 0;">
        <a href="/live" data-link>See all live trivias →</a>
      </div>
    </div>
    ${me?.is_admin ? adminQuickLinks() : ''}
  `;
}

function liveCard(t) {
  return `
    <a href="/live" data-link class="card" style="padding:13px;text-decoration:none;display:block;">
      <div class="row">
        <div class="grow">
          <b style="font-size:14px;color:#fff;font-weight:600;">${escape(t.title)}</b>
          <div style="font-size:11.5px;color:var(--mute);font-weight:500;margin-top:2px;">
            Live · ${t.participants_count} playing
          </div>
        </div>
        <div style="text-align:right;">
          <span class="tag mag">LIVE</span>
          <div style="font-family:var(--font-display);font-size:13px;margin-top:5px;color:#fff;font-weight:600;">
            ${escape(formatKobo(t.prize_kobo))}
          </div>
        </div>
      </div>
    </a>
  `;
}

function upcomingCard(t) {
  const when = t.scheduled_at
    ? new Date(t.scheduled_at).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' })
    : 'TBA';
  return `
    <a href="/live" data-link class="card" style="padding:13px;text-decoration:none;display:block;">
      <div class="row">
        <div class="grow">
          <b style="font-size:14px;color:#fff;font-weight:600;">${escape(t.title)}</b>
          <div style="font-size:11.5px;color:var(--mute);font-weight:500;margin-top:2px;">
            ${escape(when)}
          </div>
        </div>
        <div style="text-align:right;">
          <span class="tag vio">UPCOMING</span>
          <div style="font-family:var(--font-display);font-size:13px;margin-top:5px;color:#fff;font-weight:600;">
            ${escape(formatKobo(t.prize_kobo))}
          </div>
        </div>
      </div>
    </a>
  `;
}

function contestCard(c) {
  return `
    <a href="/contest/${escape(c.id)}" data-link class="card" style="padding:13px;text-decoration:none;display:block;">
      <div class="row">
        <div class="grow">
          <b style="font-size:14px;color:#fff;font-weight:600;">1-on-1 paid contest</b>
          <div style="font-size:11.5px;color:var(--mute);font-weight:500;margin-top:2px;">
            ${c.seats_filled} / ${c.capacity} seats · Entry ${escape(formatKobo(c.entry_fee_kobo))}
          </div>
        </div>
        <div style="text-align:right;">
          <span class="tag gold">POOL</span>
          <div style="font-family:var(--font-display);font-size:13px;margin-top:5px;color:#fff;font-weight:600;">
            ${escape(formatKobo(c.pool_kobo))}
          </div>
        </div>
      </div>
    </a>
  `;
}

function adminQuickLinks() {
  return `
    <h3 class="sec">Admin</h3>
    <div class="row" style="gap:9px;">
      <a href="/admin/questions" data-link class="btn ghost sm" style="flex:1;width:auto;">⚡ Question studio</a>
      <a href="/admin/live" data-link class="btn ghost sm" style="flex:1;width:auto;">🎙️ Live trivia</a>
    </div>
  `;
}

// Re-export so the router import surface stays stable.
export const _internal = { greeting, leagueLine };
